// Create Order popup - Product-first flow with config-driven IVR
import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Checkbox,
  Dialog,
  Divider,
  FormControlLabel,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { ShoppingCart } from "lucide-react";
import { useForm } from "react-hook-form";
import Table from "../../common/CustomTable";
import { SingleAlertInfo, setSingleAlertObj } from "../../../redux/slice/commonSlice";
import { IvrListDTO, searchIVRByPatientId } from "../../../redux/slice/ivr/createIVRSlice";
import { Column } from "../../../redux/slice/commonSlice";
import { IVRInfo } from "../ivr/IVRList";
import { useAppDispatch } from "../../../redux/slice/redux-hooks";
import { ListAltOutlined } from "@mui/icons-material";
import { ORDERTYPE, PRODUCT_CATEGORY, PRODUCT_CONFIG_TYPE, PRODUCT_CONFIG_CODE, PRODUCT_CONFIG_VALUE } from "../../../constants/applicationConstants";
import { fullName, formatDate, sortNames } from "../../../utils/Utility";
import CustomDropdownField from "../../common/CustomDropdownFiled";
import ConfirmationActionModal from "../../common/ConfirmationActionModal";
import { PatientInfo, getPatient, resetPatientsByUserRole, usePatientReducer } from "../../../redux/slice/patient/patientSlice";
import CustomCheckbox from "../../common/CustomCheckBox";
import { setSelectedModule } from "../../../redux/slice/authSlice";
import { useNavigation } from "../../../hooks/useNavigation";
import CustomIconButton from "../../common/CustomIconButton";
// import { getAgreementsByPracticeAndProductCategory, PracticeProductAgreement } from "../../../redux/slice/productAgreement/createAgreementSlice";
import { getProductConfigByProduct, ProductConfigRules } from "../../../service/ProductService";
import { fetchPracticeProductsDataUsingDateOfServiceAndProvider } from "../../../redux/slice/practice/practiceProductSlice";
import { useModalStyles } from "../../common/useModalStyles";
import { tokens as themeTokens } from "../../../theme";
import dayjs from "dayjs";
interface CreateOrderIVRListPopupProps {
  open: boolean;
  onClose: () => void;
  patientId?:number|null;
  practiceId?:number|null;
  practiceName?:string|null;
  patientData?:{
    firstName?:string|null
    lastName?:string|null
    middleName?:string|null
    dob?:string|null
    gender?:string|null
  };
  orderFromPatientList?:boolean|null;
  providerId?: number | null
}

interface IVRRow extends IVRInfo {
  selectCheckBox: JSX.Element;
}

type OrderPracticeProduct = {
  practiceProductId: string | number;
  productId: string | number;
  productName: string;
  agreementStatus?: string;
  providerName?: string;
  practiceProviderId?: string | number | null;
  productCategory?: string;
};

const toNumber = (value?: string | number | null): number | undefined => {
  if (value === undefined || value === null || value === "") return undefined;
  const numericValue = Number(value);
  return Number.isNaN(numericValue) ? undefined : numericValue;
};

const columns: Column[] = [
  { id: 'selectCheckBox', label: '' },
  { id: "ivrNumber", label: "IVR Number" },
  { id: "patient", label: "Patient", minWidth:130 },
  { id:'patientCode', label:"Patient Code", minWidth:130 },
  { id: 'provider', label: 'Provider', minWidth:130 },
  { id: "product", label: "Product" },
  { id: "dateCreated", label: "Date Created" },
  { id: "dateOfService", label: "Date of Service" },
  { id: "status", label: "Verification Status" },
  { id: "action", label: "Action", minWidth: 50, },
];

const getConfigValue = (
  configRules: ProductConfigRules[],
  configType: string,
  configCode: string
): string | null => {
  const rule = configRules.find(
    (r) => r.configType === configType && r.configCode === configCode
  );
  return rule?.value ?? null;
};

const CreateOrderIVRListPopup: React.FC<CreateOrderIVRListPopupProps> = ({
  open,
  onClose,patientId,orderFromPatientList,practiceId,patientData,practiceName,providerId
}) => {
  const { control, setValue } = useForm();
  const dispatch = useAppDispatch();
  const { navigate } = useNavigation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const colors = themeTokens(theme.palette.mode);
  const {
    tokens,
    isDark,
    paperSx,
    headerSx,
    iconBoxSx,
    closeIconSx,
    dropdownWrapSx,
    primaryBtnSx,
    cancelBtnSx,
    footerSx,
  } = useModalStyles();

  const [selectedIVR, setSelectedIVR] = useState<any>(null);
  const [searchedIVRList, setSearchedIVRList] = useState<IvrListDTO[] | null>(null);
  const [isIVRVerificationNotAvailable, setIsIVRVerificationNotAvailable] = useState(false);
  const { patientInfoObject } = usePatientReducer();

  const [allAgreements, setAllAgreements] = useState<OrderPracticeProduct[]>([]);
  const [selectedProduct, setSelectedProduct] = useState<OrderPracticeProduct | null>(null);
  const [productConfigRules, setProductConfigRules] = useState<ProductConfigRules[]>([]);
  const [showAgreementAlert, setShowAgreementAlert] = useState(false);

  const selectedProductAgreementStatus = useMemo(() => {
    return selectedProduct?.agreementStatus?.trim() || "Not Available";
  }, [selectedProduct]);

  const isSelectedProductAgreementExecuted = useMemo(() => {
    if (!selectedProduct) return true;
    return selectedProduct.agreementStatus?.toLowerCase() === "executed";
  }, [selectedProduct]);

  const ivrConfig = useMemo(
    () => getConfigValue(productConfigRules, PRODUCT_CONFIG_TYPE.ORDER_REQUIREMENT, PRODUCT_CONFIG_CODE.IVR),
    [productConfigRules]
  );

  const orderWorkflowType = useMemo(
    () => getConfigValue(productConfigRules, PRODUCT_CONFIG_TYPE.ORDER_WORKFLOW, PRODUCT_CONFIG_CODE.TYPE),
    [productConfigRules]
  );

  const showIvrSection = useMemo(() => {
    if (!selectedProduct) return false;
    return ivrConfig === PRODUCT_CONFIG_VALUE.REQUIRED || ivrConfig === PRODUCT_CONFIG_VALUE.OPTIONAL;
  }, [selectedProduct, ivrConfig]);

  const isIvrRequired = useMemo(() => ivrConfig === PRODUCT_CONFIG_VALUE.REQUIRED, [ivrConfig]);

  const canContinue = useMemo(() => {
    if (!selectedProduct) return false;
    if (!isSelectedProductAgreementExecuted) return false;
    if (!showIvrSection) return true; // IVR excluded
    if (isIvrRequired) return !!selectedIVR;
    return !!selectedIVR || isIVRVerificationNotAvailable; // optional
  }, [selectedProduct, isSelectedProductAgreementExecuted, showIvrSection, isIvrRequired, selectedIVR, isIVRVerificationNotAvailable]);

  useEffect(() => {
    if (!open) return;
    const init = async () => {
      if (patientId) {
        try {
          await dispatch(getPatient({ patientId: patientId.toString() })).unwrap();
        } catch (err) {
          console.error("Failed to load patient:", err);
        }
      }
      if (practiceId) {
        try {
          // CS-1775: Fetch products using IVR practice product API payload
          const formattedDate = dayjs().format("MM/DD/YYYY");
          const response = await dispatch(
            fetchPracticeProductsDataUsingDateOfServiceAndProvider({
              practiceId,
              dateOfService: formattedDate,
              providerId: 0,
              moduleName: "",
            })
          );

          // CS-1775: Old agreement API logic kept commented for reference during rollout
          // const [advRes, conRes] = await Promise.all([
          //   dispatch(getAgreementsByPracticeAndProductCategory({ practiceId, productCategory: PRODUCT_CATEGORY.ADVANCED })),
          //   dispatch(getAgreementsByPracticeAndProductCategory({ practiceId, productCategory: PRODUCT_CATEGORY.CONSERVATIVE })),
          // ]);
          // setAllAgreements([...(advRes?.payload ?? []), ...(conRes?.payload ?? [])]);
          setAllAgreements(response.meta.requestStatus === "fulfilled" ? response.payload?.data ?? [] : []);
        } catch (err) {
          console.error("Failed to fetch products:", err);
          setAllAgreements([]);
        }
      }
    };
    init();
  }, [open]);

  useEffect(() => {
    if (!open) {
      setSearchedIVRList(null);
      setSelectedIVR(null);
      setSelectedProduct(null);
      setProductConfigRules([]);
      setAllAgreements([]);
      setIsIVRVerificationNotAvailable(false);
      setValue("product", "");
      dispatch(resetPatientsByUserRole());
    }
  }, [open]);

  const handleProductSelection = async (practiceProductId: number) => {
    const agreement = allAgreements.find((a) => toNumber(a.practiceProductId) === practiceProductId);
    setSelectedProduct(agreement ?? null);
    setSelectedIVR(null);
    setSearchedIVRList(null);
    setIsIVRVerificationNotAvailable(false);
    // CS-1775: Stop order workflow at product selection when agreement is not executed
    setShowAgreementAlert(
      agreement?.agreementStatus !== undefined &&
      agreement.agreementStatus?.toLowerCase() !== "executed"
    );

    if (agreement?.agreementStatus?.toLowerCase() !== "executed") {
      setProductConfigRules([]);
      return;
    }

    if (!agreement?.productId) return;

    try {
      const cfgRes = await getProductConfigByProduct(Number(agreement.productId));
      const rules: ProductConfigRules[] = cfgRes?.data?.data ?? [];
      setProductConfigRules(rules);

      const ivrCfg = getConfigValue(rules, PRODUCT_CONFIG_TYPE.ORDER_REQUIREMENT, PRODUCT_CONFIG_CODE.IVR);
      if ((ivrCfg === PRODUCT_CONFIG_VALUE.REQUIRED || ivrCfg === PRODUCT_CONFIG_VALUE.OPTIONAL) && patientId) {
        const ivrRes = await dispatch(searchIVRByPatientId({ patientId, requireAll: false }));
        if (ivrRes?.meta?.requestStatus === "fulfilled" && ivrRes?.payload?.data?.length > 0) {
          // Filter IVRs to only show those matching the selected product
          const filtered = ivrRes.payload.data.filter(
            (ivr: IvrListDTO) => ivr.productId === Number(agreement.productId)
          );
          setSearchedIVRList(filtered.length > 0 ? filtered : null);
          if (filtered.length === 0 && ivrCfg === PRODUCT_CONFIG_VALUE.REQUIRED) {
            dispatch(setSingleAlertObj({ message: "No IVR found for this patient with the selected product", alertType: "error" } as SingleAlertInfo));
          }
        } else {
          setSearchedIVRList(null);
          if (ivrCfg === PRODUCT_CONFIG_VALUE.REQUIRED) {
            dispatch(setSingleAlertObj({ message: "No IVR found for this patient", alertType: "error" } as SingleAlertInfo));
          }
        }
      }
    } catch (err) {
      console.error("Failed to fetch product config:", err);
      setProductConfigRules([]);
    }
  };

  const handleCheckbox = (e: React.ChangeEvent<HTMLInputElement>, row: any) => {
    const { checked } = e.target;
    if(checked) {
      setSelectedIVR({
        ...row,
        providerName : row.providerName
      });
    } else {
      setSelectedIVR(null)
    }
  }

  const handleIVRNotAvailable = (e: React.ChangeEvent<HTMLInputElement>) => {
    setIsIVRVerificationNotAvailable(e.target.checked);
    if (e.target.checked) {
      setSelectedIVR(null);
    }
  };

  const checkboxComponent = (row: IVRInfo): JSX.Element => {
    return (
      <CustomCheckbox 
        control={control} 
        onChange={(e) => handleCheckbox(e, row)} 
        checked={row.ivrId === selectedIVR?.ivrId}
        isDisabled={isIVRVerificationNotAvailable}
      />
    );
  };

  const searchedIvrInfo: IVRRow[] = useMemo(() => {
    if (!searchedIVRList) return [];
    return searchedIVRList.map((ivr: any) => ({
      selectCheckBox: checkboxComponent(ivr),
      practice: ivr.practiceName,
      patient: fullName(ivr.title, ivr.patientFirstName, ivr.patientMiddleName, ivr.patientLastName),
      product: ivr.productName,
      provider: ivr.providerName,
      dateCreated: formatDate(ivr.auditTimestamp),
      createdDate: ivr.createdDate,
      dateOfService: ivr.dateOfService,
      status: ivr.verificationStatus,
      locationId: ivr.locationId,
      productId: ivr.productId,
      practiceId: ivr.practiceId,
      ivrId: ivr.ivrId,
      ivrNumber: ivr.ivrNumber,
      patientId: ivr.patientId,
      patientDOB: ivr.patientDOB,
      practiceProductId: ivr.practiceProductId,
      ivrDocumentId: ivr.ivrDocumentId,
      patientCode: ivr.patientCode,
    }));
  }, [searchedIVRList, selectedIVR, isIVRVerificationNotAvailable]);

  /* ── Product dropdown options (deduplicated by productId) ── */
  const productOptions = useMemo(() => {
    const seen = new Set<string | number>();
    return allAgreements
      .filter((a) => {
        if (seen.has(a.productId)) return false;
        seen.add(a.productId);
        return true;
      })
      .map((a) => ({ value: a.practiceProductId, label: a.productName }));
  }, [allAgreements]);

  const actionComponent = (rowObj: IVRInfo): JSX.Element => {
    return (
      <>
        <CustomIconButton
          color="info"
          size="small"
          variant="outlined"
          onClick={() => {
            const cleanIvrInfo = {
              ivrId: rowObj.ivrId,
              ivrDocumentId: rowObj.ivrDocumentId,
              ivrNumber: rowObj.ivrNumber,
              patient: rowObj.patient,
              patientDOB: rowObj.patientDOB,
              dateCreated: rowObj.dateCreated,
              status: rowObj.status,
              patientId: rowObj.patientId,
              product: rowObj.product
            };
            navigate("/ivr_details", { state: { ivrInfo: cleanIvrInfo } });
          }}
          tooltipMessage="Detail"
          tooltipPlacement="top"
        >
          <ListAltOutlined fontSize="small" />
        </CustomIconButton>
      </>
    );
  };

  const createOrder = () => {
    const isCollagenWorkflow = orderWorkflowType === PRODUCT_CONFIG_VALUE.COLLAGEN;
    const prodCategory = selectedIVR?.productCategory ?? selectedProduct?.productCategory;
    const isCollagenByCategory = prodCategory?.toLowerCase().includes(PRODUCT_CATEGORY.CONSERVATIVE.toLowerCase());
    const isCollagen = orderWorkflowType ? isCollagenWorkflow : isCollagenByCategory;

    const resolvedProductId = selectedIVR?.productId ?? selectedProduct?.productId;
    const resolvedProductName = selectedIVR?.productName ?? selectedProduct?.productName;
    const resolvedPracticeProductId = selectedIVR?.practiceProductId ?? selectedProduct?.practiceProductId;

    if (selectedIVR) {
      if (isCollagen) {
        navigate("/ancillaryorder", {
          state: {
            ivrId: selectedIVR.ivrId,
            practiceId,
            practiceName,
            patientId,
            providerId: selectedIVR.providerId,
            providerName: selectedIVR.providerName,
            orderType: ORDERTYPE.DIRECT_ORDER,
            patientData: patientInfoObject,
          },
        });
      } else {
        navigate("/order", {
          state: {
            orderType: ORDERTYPE.DIRECT_ORDER,
            orderDetails: {
              ...selectedIVR,
              productId: resolvedProductId,
              productName: resolvedProductName,
              practiceProductId: resolvedPracticeProductId,
            },
          },
        });
      }
    } else {
      if (isCollagen) {
        navigate("/ancillaryorder", {
          state: {
            orderType: ORDERTYPE.DIRECT_ORDER_WITHOUT_IVR,
            practiceId,
            practiceName,
            patientId,
            patientData: patientInfoObject,
            providerId,
            productId: resolvedProductId,
          },
        });
      } else {
        navigate("/order", {
          state: {
            practiceId,
            patientId,
            patientData,
            orderType: ORDERTYPE.DIRECT_ORDER_WITHOUT_IVR,
            productId: resolvedProductId,
            practiceProductId: resolvedPracticeProductId,
          },
        });
      }
    }
    dispatch(setSelectedModule("Order"));
    onClose();
  };

  const handleClose = () => {
    onClose();
  };

  const labelSx = {
    fontSize: "0.82rem",
    fontWeight: 600,
    color: tokens.textSecondary,
    mb: 0.7,
    display: "block",
  };

  const stepNumberSx = (active: boolean) => ({
    width: 24,
    height: 24,
    borderRadius: "50%",
    backgroundColor: active ? tokens.accent : isDark ? "rgba(255,255,255,0.06)" : "#e2e8f0",
    color: active ? "#fff" : tokens.textSecondary,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "0.72rem",
    fontWeight: 700,
    flexShrink: 0,
    transition: "all 0.25s",
  });

  const thSx = {
    fontSize: "0.65rem",
    fontWeight: 700,
    color: tokens.textSecondary,
    textTransform: "uppercase" as const,
    letterSpacing: "0.04em",
    py: 0.8,
    px: 0.8,
    borderBottom: `1px solid ${tokens.border}`,
    backgroundColor: isDark ? "#1a1e2e" : "#f8fafc",
    whiteSpace: "nowrap" as const,
    position: "sticky" as const,
    top: 0,
    zIndex: 2,
    textAlign: "left" as const,
  };

  const tdSx = {
    fontSize: "0.75rem",
    fontWeight: 500,
    color: tokens.textPrimary,
    py: 0.6,
    px: 0.8,
    borderBottom: `1px solid ${tokens.border}`,
    whiteSpace: "nowrap" as const,
    overflow: "hidden",
    textOverflow: "ellipsis",
    textAlign: "left" as const,
  };

  const trSx = (isSelected: boolean) => ({
    cursor: "pointer",
    backgroundColor: isSelected
      ? isDark ? "rgba(20,184,166,0.08)" : "rgba(20,184,166,0.04)"
      : "transparent",
    transition: "background-color 0.15s",
    "&:hover": {
      backgroundColor: isSelected
        ? isDark ? "rgba(20,184,166,0.10)" : "rgba(20,184,166,0.06)"
        : isDark ? "rgba(255,255,255,0.03)" : "rgba(20,184,166,0.02)",
    },
    "&:last-child td": { borderBottom: "none" },
  });

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{ sx: paperSx(isMobile) }}
      onClick={(e) => e.stopPropagation()}
    >
      <Box sx={headerSx}>
        <Box sx={iconBoxSx(tokens.accent)}>
          <ShoppingCart size={20} color={tokens.accent} strokeWidth={1.8} />
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography
            fontWeight={700}
            sx={{ fontSize: "1.1rem", color: tokens.textPrimary, lineHeight: 1.3 }}
          >
            Create Order
          </Typography>
          <Typography sx={{ fontSize: "0.8rem", color: tokens.textSecondary, mt: 0.15 }}>
            Select a product and proceed with order placement
          </Typography>
        </Box>
        <IconButton onClick={handleClose} size="small" sx={closeIconSx}>
          <CloseIcon sx={{ fontSize: "1rem" }} />
        </IconButton>
      </Box>

      <Box
        sx={{
          px: { xs: 2, sm: 2.8 },
          py: 2.4,
          display: "flex",
          flexDirection: "column",
          gap: 2.5,
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          overflowX: "hidden",
          scrollbarGutter: "stable",
          "&::-webkit-scrollbar": { width: 5 },
          "&::-webkit-scrollbar-track": { background: "transparent" },
          "&::-webkit-scrollbar-thumb": {
            background: isDark ? "rgba(255,255,255,0.10)" : "#CBD5E1",
            borderRadius: 4,
          },
        }}
      >
        {/* Patient info card */}
        <Box
          sx={{
            backgroundColor: tokens.cardBg,
            border: `1px solid ${tokens.border}`,
            borderRadius: "12px",
            px: 2.2,
            py: 1.4,
            display: "flex",
            alignItems: "center",
            gap: 1.2,
          }}
        >
          <Box
            sx={{
              width: 34,
              height: 34,
              borderRadius: "8px",
              backgroundColor: `${tokens.accent}1a`,
              border: `1px solid ${tokens.accent}33`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={tokens.accent} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
              <circle cx="12" cy="7" r="4" />
            </svg>
          </Box>
          <Box>
            <Typography sx={{ fontSize: "0.72rem", color: tokens.textSecondary, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em" }}>
              Patient
            </Typography>
            <Typography sx={{ fontSize: "0.92rem", fontWeight: 700, color: tokens.textPrimary }}>
              {fullName("", patientData?.firstName, patientData?.middleName, patientData?.lastName) || "—"}
            </Typography>
          </Box>
        </Box>

        {/* Step 1 — Product selection */}
        <Box>
          <Box display="flex" alignItems="center" gap={1} mb={1}>
            <Box sx={stepNumberSx(true)}>1</Box>
            <Typography sx={{ fontSize: "0.85rem", fontWeight: 700, color: tokens.textPrimary }}>
              Select Product
              <Typography component="span" sx={{ color: tokens.accent, fontSize: "0.85rem", ml: 0.3 }}>*</Typography>
            </Typography>
          </Box>
          <Box sx={{ ...dropdownWrapSx(), pl: 4.2 }}>
            <CustomDropdownField
              name="product"
              control={control}
              options={productOptions}
              setValues={setValue}
              size="small"
              placeholderText={productOptions.length === 0 ? "No products available" : "Choose a product..."}
              sortOptions={false}
              onChangeSelect={(_e: any, val: any) => {
                // Fixed Create Order button state on product deselect
                if (val) {
                  handleProductSelection(Number(val));
                } else {
                  setSelectedProduct(null);
                }
              }}
            />
          </Box>
        </Box>

        {/* Step 2 — IVR section (config-driven) */}
        {showIvrSection && isSelectedProductAgreementExecuted && (
          <>
            <Divider sx={{ borderColor: tokens.border }} />

            <Box>
              <Box display="flex" alignItems="center" gap={1} mb={1.2}>
                <Box sx={stepNumberSx(!!selectedIVR || isIVRVerificationNotAvailable)}>2</Box>
                <Typography sx={{ fontSize: "0.85rem", fontWeight: 700, color: tokens.textPrimary }}>
                  Select IVR
                  {isIvrRequired && (
                    <Typography component="span" sx={{ fontSize: "0.85rem", color: tokens.accent, ml: 0.3 }}>*</Typography>
                  )}
                </Typography>
                {searchedIvrInfo.length > 0 && (
                  <Box sx={{
                    ml: "auto",
                    px: 1,
                    py: 0.2,
                    borderRadius: "6px",
                    backgroundColor: isDark ? "rgba(255,255,255,0.05)" : "#f1f5f9",
                    border: `1px solid ${tokens.border}`,
                  }}>
                    <Typography sx={{ fontSize: "0.7rem", fontWeight: 600, color: tokens.textSecondary }}>
                      {searchedIvrInfo.length} {searchedIvrInfo.length === 1 ? "record" : "records"}
                    </Typography>
                  </Box>
                )}
              </Box>

              {/* IVR table — hidden when "not available" is checked */}
              {!isIVRVerificationNotAvailable && (
                <Box sx={{ pl: { xs: 0, sm: 4.2 } }}>
                  {searchedIvrInfo.length > 0 ? (
                    <Box
                      sx={{
                        border: `1px solid ${tokens.border}`,
                        borderRadius: "12px",
                        overflow: "hidden",
                      }}
                    >
                      <Box
                        sx={{
                          maxHeight: 300,
                          overflowY: "auto",
                          overflowX: "auto",
                          "&::-webkit-scrollbar": { width: 4, height: 4 },
                          "&::-webkit-scrollbar-track": { background: "transparent" },
                          "&::-webkit-scrollbar-thumb": { background: isDark ? "rgba(255,255,255,0.10)" : "#CBD5E1", borderRadius: 4 },
                        }}
                      >
                        <Box component="table" sx={{ width: "100%", borderCollapse: "collapse", tableLayout: "auto" }}>
                          <Box component="thead">
                            <Box component="tr">
                              <Box component="th" sx={{ ...thSx, width: 10, px: 0.3}}></Box>
                              <Box component="th" sx={thSx}>IVR Number</Box>
                              <Box component="th" sx={thSx}>Patient</Box>
                              <Box component="th" sx={thSx}>Patient Code</Box>
                              <Box component="th" sx={thSx}>Provider</Box>
                              <Box component="th" sx={thSx}>Product</Box>
                              <Box component="th" sx={thSx}>Created</Box>
                              {/* <Box component="th" sx={thSx}>DOS</Box> */}
                              <Box component="th" sx={thSx}>Status</Box>
                              <Box component="th" sx={{ ...thSx, width: 36, px: 0.3, textAlign: "center" }}></Box>
                            </Box>
                          </Box>
                          <Box component="tbody">
                            {searchedIvrInfo.map((row: any) => {
                              const isSelected = row.ivrId === selectedIVR?.ivrId;
                              return (
                                <Box
                                  component="tr"
                                  key={row.ivrId}
                                  onClick={() => {
                                    if (isIVRVerificationNotAvailable) return;
                                    if (isSelected) {
                                      setSelectedIVR(null);
                                    } else {
                                      setSelectedIVR({ ...row, providerName: row.provider });
                                    }
                                  }}
                                  sx={trSx(isSelected)}
                                >
                                  <Box component="td" sx={{
                                    ...tdSx, width: 36, px: 0.3, textAlign: "center", overflow: "visible",
                                    "& .MuiFormControlLabel-root": { m: 0, mr: 0 },
                                    "& .MuiCheckbox-root": { p: 0.3 },
                                    "& .MuiSvgIcon-root": { fontSize: "1.15rem" },
                                    "& .MuiFormControlLabel-label": { display: "none" },
                                  }}>
                                    {checkboxComponent(row)}
                                  </Box>
                                  <Box component="td" sx={{ ...tdSx, fontWeight: 600 }}>{row.ivrNumber}</Box>
                                  <Box component="td" sx={tdSx}>{row.patient || "—"}</Box>
                                  <Box component="td" sx={tdSx}>{row.patientCode || "—"}</Box>
                                  <Box component="td" sx={tdSx}>{row.provider || "—"}</Box>
                                  <Box component="td" sx={tdSx}>{row.product || "—"}</Box>
                                  <Box component="td" sx={tdSx}>{row.dateCreated || "—"}</Box>
                                  {/* <Box component="td" sx={tdSx}>{row.dateOfService || "—"}</Box> */}
                                  <Box component="td" sx={{ ...tdSx, overflow: "visible" }}>{row.status || "—"}</Box>
                                  <Box component="td" sx={{ ...tdSx, width: 36, overflow: "visible", textAlign: "center", px: 0 }}>
                                    {actionComponent(row)}
                                  </Box>
                                </Box>
                              );
                            })}
                          </Box>
                        </Box>
                      </Box>
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        border: `1px dashed ${tokens.border}`,
                        borderRadius: "12px",
                        py: 3,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: 0.8,
                      }}
                    >
                      <Typography sx={{ fontSize: "0.82rem", color: tokens.textSecondary, fontWeight: 500 }}>
                        No IVR(s) Found
                      </Typography>
                    </Box>
                  )}
                </Box>
              )}

              {/* "IVR not available" checkbox — only for OPTIONAL */}
              {!isIvrRequired && (
                <Box sx={{ pl: 4.2, mt: 1 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={isIVRVerificationNotAvailable}
                        onChange={handleIVRNotAvailable}
                        size="small"
                        sx={{
                          color: tokens.textSecondary,
                          "&.Mui-checked": { color: tokens.accent },
                          "& .MuiSvgIcon-root": { fontSize: "1.15rem" },
                        }}
                      />
                    }
                    label={
                      <Typography sx={{ fontSize: "0.8rem", color: tokens.textPrimary, fontWeight: 500 }}>
                        IVR verification NOT available in system
                      </Typography>
                    }
                  />
                </Box>
              )}
            </Box>
          </>
        )}
      </Box>

      <Box sx={footerSx}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 1.5,
            flexDirection: { xs: "column", sm: "row" },
          }}
        >
          <Box
            component="button"
            onClick={handleClose}
            sx={{
              ...cancelBtnSx,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 0.8,
              px: 3,
              py: 1.2,
              cursor: "pointer",
              order: { xs: 2, sm: 1 },
            }}
          >
            Cancel
          </Box>
          <Box
            component="button"
            disabled={!canContinue}
            onClick={createOrder}
            sx={{
              ...primaryBtnSx,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 0.8,
              px: 3,
              py: 1.2,
              border: "none",
              cursor: canContinue ? "pointer" : "not-allowed",
              color: "#fff",
              opacity: canContinue ? 1 : 0.45,
              backgroundColor: canContinue ? tokens.accent : tokens.textSecondary,
              boxShadow: canContinue ? "0 4px 18px rgba(20,184,166,0.35)" : "none",
              order: { xs: 1, sm: 2 },
            }}
          >
            <ShoppingCart size={15} strokeWidth={2} />
            Create Order
          </Box>
        </Box>
      </Box>
      {showAgreementAlert && (
        <ConfirmationActionModal
          open={showAgreementAlert}
          onClose={() => setShowAgreementAlert(false)}
          onConfirm={() => setShowAgreementAlert(false)}
          title="Warning"
          subtitle="Agreement required"
          message={
            <>
              An active agreement for <strong>{selectedProduct?.productName ?? "this product"}</strong> could not be found. Please contact support for assistance.
            </>
          }
          confirmText="OK"
          headerIcon={<WarningAmberIcon sx={{ color: "orange" }} />}
          showCancelButton={false}
        />
      )}
    </Dialog>
  );
};

export default CreateOrderIVRListPopup;
